"use client";

import { useEffect, useMemo, useState } from "react";
import { CalendarClock, Timer } from "lucide-react";
import {
  formatBatchDate,
  formatBatchTime,
  getBatchCountdown,
  getNextBatchStart,
} from "@/lib/batch-schedule";
import { cn } from "@/lib/utils";

function pad(n: number) {
  return String(n).padStart(2, "0");
}

export function BatchCountdown({ compact = false }: { compact?: boolean }) {
  const target = useMemo(() => getNextBatchStart(), []);
  const [countdown, setCountdown] = useState(() => getBatchCountdown(target));

  useEffect(() => {
    const tick = () => setCountdown(getBatchCountdown(target));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [target]);

  const units = [
    { label: "Days", value: countdown.days },
    { label: "Hours", value: countdown.hours },
    { label: "Minutes", value: countdown.minutes },
    { label: "Seconds", value: countdown.seconds },
  ];

  const started = countdown.totalMs <= 0;

  return (
    <div
      className={cn(
        "w-full rounded-3xl border border-mont-violet/25 bg-gradient-to-br from-mont-violet/10 via-card/60 to-mont-orange/10 shadow-[0_20px_60px_-24px_rgba(139,92,246,0.35)] backdrop-blur-md",
        compact ? "p-5" : "p-6 sm:p-8",
      )}
    >
      <div
        className={cn(
          "mb-5 flex gap-3",
          compact ? "flex-col" : "flex-wrap items-center justify-between gap-4",
        )}
      >
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "flex shrink-0 items-center justify-center rounded-2xl border border-mont-violet/30 bg-mont-violet/10",
              compact ? "h-10 w-10" : "h-11 w-11",
            )}
          >
            <Timer className={cn("text-mont-violet", compact ? "h-4 w-4" : "h-5 w-5")} />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-mont-violet sm:text-xs">
              Next batch starts in
            </p>
            <p className={cn("text-muted-foreground", compact ? "text-xs" : "text-sm")}>
              {formatBatchDate(target)} · {formatBatchTime(target)} IST
            </p>
          </div>
        </div>
        {!compact ? (
          <div className="inline-flex items-center gap-2 rounded-full border border-border/80 bg-background/60 px-3 py-1.5 text-xs font-medium text-muted-foreground">
            <CalendarClock className="h-3.5 w-3.5 text-mont-orange" />
            Classroom intake
          </div>
        ) : null}
      </div>

      {started ? (
        <p className={cn("text-center font-semibold text-foreground", compact ? "text-sm" : "text-lg")}>
          This batch window is open — contact us to reserve your seat.
        </p>
      ) : (
        <div className={cn("grid gap-2.5", compact ? "grid-cols-2" : "grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4")}>
          {units.map((unit) => (
            <div
              key={unit.label}
              className={cn(
                "flex flex-col items-center rounded-2xl border border-border/70 bg-background/70",
                compact ? "px-3 py-3.5" : "px-4 py-5",
              )}
            >
              <span
                className={cn(
                  "font-heading font-bold tabular-nums text-foreground",
                  compact ? "text-2xl" : "text-3xl sm:text-4xl",
                  unit.label === "Seconds" && "text-mont-violet",
                )}
                suppressHydrationWarning
              >
                {pad(unit.value)}
              </span>
              <span className="mt-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {unit.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
